import { GridCellParams, GridValidRowModel } from '@mui/x-data-grid-pro'
import TextInput from 'ext-ui/components/TextInput/TextInput'
import React from 'react'
import DataGridCellWrapper from 'src/components/DataGrid/cells/DataGridCellWrapper/DataGridCellWrapper'
import { css } from 'styled-components'

type SplitValueInputCellProps<Row extends GridValidRowModel> = {
  params: GridCellParams<Row>
  value?: string
  placeholder?: string
  onChange: (value: string) => void
  onBlur?: (value: string) => void
  decimals?: number
  disabled?: boolean
}

const SplitValueInputCell = <Row extends GridValidRowModel>(
  props: SplitValueInputCellProps<Row>,
): React.ReactElement => {
  const { params, value, onChange, placeholder, onBlur, decimals = 2, disabled } =
    props
  const [isFocused, setIsFocused] = React.useState(false)
  const [localValue, setLocalValue] = React.useState<string>(
    formatDisplayValue(value, decimals),
  )

  React.useEffect(() => {
    if (!isFocused) {
      setLocalValue(formatDisplayValue(value, decimals))
    }
  }, [decimals, isFocused, value])

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation()
    const nextValue = event.target.value
    setLocalValue(nextValue)
    onChange(nextValue)
  }

  const stopPropagation = (
    event:
      | React.MouseEvent<HTMLInputElement>
      | React.KeyboardEvent<HTMLInputElement>
      | React.FocusEvent<HTMLInputElement>,
  ) => {
    event.stopPropagation()
  }

  return (
    <DataGridCellWrapper params={params} compact>
      <div
        css={css`
          width: 100%;
        `}
      >
        <TextInput
          variant="small"
          type="text"
          inputMode="decimal"
          placeholder={placeholder ?? '0.00'}
          value={
            isFocused
              ? localValue
              : formatDisplayValue(localValue, decimals)
          }
          disabled={disabled}
          onChange={handleChange}
          onClick={stopPropagation}
          onFocus={(event) => {
            stopPropagation(event)
            setIsFocused(true)
          }}
          onBlur={(event) => {
            stopPropagation(event)
            setIsFocused(false)
            onBlur?.(event.target.value)
          }}
          onKeyDown={stopPropagation}
          css={css`
            input {
              padding-left: 8px;
              padding-right: 8px;
              text-align: right;
            }
          `}
        />
      </div>
    </DataGridCellWrapper>
  )
}

export default SplitValueInputCell

const formatDisplayValue = (
  value: string | undefined,
  decimals: number,
): string => {
  if (!value) return ''
  const numericValue = Number.parseFloat(value)
  if (Number.isNaN(numericValue)) return value
  return numericValue.toFixed(decimals)
}
